// «Контроль» — таблица открытых пунктов (003: сценарий 5; FR-017, FR-035 – FR-037): открытые
// пункты последних разобранных версий доступных документов со статусом на дату отсчёта.
//
// Приёмка 0.3.1 (T041 – T044), по «Табелю» как «Журнал действий»: над таблицей — панель
// поиск → «Статус» по группам листа → «Документ» → «Срок до» → «Дата отсчёта» → «Сортировка» →
// «Колонки» → «Сбросить фильтры»; условия — в адресе страницы под именами запроса control.items
// (search.ts), считает их сервер. Группы в два уровня: протокол «П-1 · название · лист 17.09.26»
// со счётчиками «просрочено» и «горит», внутри — разделы; в колонке «Источник» — только номер
// пункта. Клик и Enter по строке открывают боковую панель пункта (ItemDrawer.tsx).
//
// 004 (сценарий 2.2; FR-008, FR-010, FR-027): переключатель «Открытые / Закрыто ИИ» над таблицей.
// В «Закрыто ИИ» — бейдж «Закрыто · ИИ 0,92» и основание решения, фильтра «Статус» нет. У пункта
// на проверке под бейджем «Проверить · ИИ 0,85» — основание (принцип III).
//
// 005 (T022; FR-011): после «Статуса» — «Ответственный»: запись справочника, responsibleId
// в адресе и в запросе (ResponsibleFilter.tsx); тем, кто читает справочник.
//
// 014 (T027; FR-004): пункты протоколов в режиме ведения — в том же списке: строка — по itemId,
// группа — «П-N · название · таблица протокола», ответственные — из полей пункта; в панели пункта
// «Открыть в таблице». «Документ» предлагает и протоколы в режиме ведения без разобранной версии.
//
// 047 (DNS-03, DNS-04): «Дата отсчёта» на экране одна — кнопкой панели с выбранной датой
// (asOfButtonLabel); пилюли под заголовком нет.
//
// 045 (OPN-21; research Р9): пункты приходят вместе со страницей — их берёт загрузчик маршрута
// (routes/_app/control.tsx), карточка встаёт сразу со строками. Пунктов нет («Повторить» после
// сбоя): первые 0,3 с под панелью — место без рамки таблицы, дальше — скелет строк в форме
// таблицы (TableSkeleton).
//
// 046 (T022, T023; PHN-19 – PHN-21; contracts/ui.md): до 640 px над списком одна строка — поиск
// и «Фильтры» (phone-filters.tsx). Кнопки панели шире 640 px — те же, в том же порядке — рядами
// окна «Фильтры» снизу: «Статус» (в «Открытых»), «Ответственный», «Документ», «Срок до», «Дата
// отсчёта», «Сортировка»; «Колонок» на телефоне нет, как и раньше: строки — карточки. Включённые
// условия — лентой чипов под строкой поиска, «Дата отсчёта» — чипом, когда она не «по листу».
// Число на «Фильтры» — условия, которые сужают список: без поиска, даты отсчёта и сортировки.
import { CONTROL_SEARCH_MAX, controlGroupFilters, fieldMessages } from '@kp/contracts';
import type { AsOfMode, ControlGroupFilter, ControlItem, ControlSort } from '@kp/contracts';
import {
  Button,
  DataTable,
  DateField,
  Dropdown,
  DropdownCheckboxItem,
  DropdownLabel,
  DropdownMessage,
  DropdownRadioGroup,
  DropdownRadioItem,
  EmptyState,
  FilterChip,
  Icon,
  PageHeader,
  Popover,
  SearchField,
  Segmented,
  SkeletonBar,
  Truncate,
  cx,
  useDelayedFlag,
  useIsPhone,
} from '@kp/ui';
import type { DataTableColumn } from '@kp/ui';
import {
  AlarmClock,
  ArrowUpDown,
  CalendarClock,
  CheckCheck,
  ClipboardList,
  Columns3,
  Flame,
  Sparkles,
} from '@kp/ui/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { apiQuery } from '../../api/client.ts';
import { errorView } from '../../app/errors.ts';
import { formatDate, shiftDate, todayIn } from '../../app/format.ts';
import { readsDirectory } from '../../shell/permissions.ts';
import { useMe, useTimeZone } from '../../shell/session.ts';
import { LoadErrorState, useQueryFailure } from '../load-error.tsx';
import { PhoneChips, PhoneFilters } from '../phone-filters.tsx';
import { ItemDrawer } from './ItemDrawer.tsx';
import { ResponsibleFilter } from './ResponsibleFilter.tsx';
import { TableSkeleton } from './TableSkeleton.tsx';
import './control.css';
import {
  StatusBadge,
  basisOf,
  dueText,
  groupByProtocol,
  isReview,
  itemKey,
  sourceOf,
} from './items.tsx';
import {
  controlGroupFilterLabels,
  controlQuery,
  hasControlFilters,
  normalizeControlSearch,
  parseGroups,
  withoutFilters,
} from './search.ts';
import type { ControlSearch, ControlView } from './search.ts';

const SEARCH_DELAY_MS = 250;

export const controlSortLabels = {
  source: 'По протоколу',
  due: 'По сроку',
} as const satisfies Record<ControlSort, string>;

const asOfLabels = {
  sheet: 'По листу недели',
  today: 'На сегодня',
} as const satisfies Record<AsOfMode, string>;

const viewOptions = [
  { value: 'open', label: 'Открытые' },
  { value: 'aiClosed', label: 'Закрыто ИИ' },
  { value: 'markClosed', label: 'Закрыто по отметке' },
] as const;

// Ширины — у всех колонок, кроме текста пункта: таблицы разделов с фиксированной раскладкой
// (screens.css, kp-control-table) совпадают по колонкам, номер и бейдж статуса видны целиком
// при 1440 px, текст пункта получает остаток и сокращается многоточием.
const sourceColumn: DataTableColumn<ControlItem> = {
  id: 'source',
  header: 'Источник',
  value: sourceOf,
  width: '5.5rem',
};
const textColumn: DataTableColumn<ControlItem> = {
  id: 'text',
  header: 'Пункт',
  value: (item) => item.text,
  hideable: false,
};
const dueColumn: DataTableColumn<ControlItem> = {
  id: 'due',
  header: 'Срок',
  value: dueText,
  numeric: true,
  width: '6.5rem',
};
const responsibleColumn: DataTableColumn<ControlItem> = {
  id: 'responsible',
  header: 'Ответственный',
  // 028 (FR-027): пункт без людей — его контрагенты с подписью впереди, как в листе контроля.
  value: (item) =>
    item.responsibleFrom === 'contractor' && item.responsible
      ? `Контрагент: ${item.responsible}`
      : item.responsible,
  width: '11rem',
};

const openColumns: DataTableColumn<ControlItem>[] = [
  sourceColumn,
  textColumn,
  dueColumn,
  {
    id: 'transfers',
    header: 'Переносов',
    value: (item) => item.transfers,
    numeric: true,
    width: '6rem',
  },
  responsibleColumn,
  {
    id: 'status',
    header: 'Статус',
    // У пункта на проверке под бейджем — основание: что подтвердить на планёрке (FR-027).
    cell: (item) => (
      <div className="kp-control-status">
        <StatusBadge item={item} />
        {isReview(item) ? (
          <Truncate text={basisOf(item)} className="t-muted kp-control-basis" />
        ) : null}
      </div>
    ),
    width: '17.5rem',
    hideable: false,
  },
];

// Закрытые ИИ: вместо переносов — основание решения; статус — бейдж «Закрыто · ИИ 0,92».
const aiClosedColumns: DataTableColumn<ControlItem>[] = [
  sourceColumn,
  textColumn,
  { id: 'basis', header: 'Основание', value: basisOf, width: '30%' },
  dueColumn,
  responsibleColumn,
  {
    id: 'status',
    header: 'Статус',
    cell: (item) => <StatusBadge item={item} />,
    width: '12rem',
    hideable: false,
  },
];

// Закрытые отметкой выполнения (010, FR-048): основание — текст ячейки с отметкой.
const markClosedColumns: DataTableColumn<ControlItem>[] = [
  sourceColumn,
  textColumn,
  { id: 'mark', header: 'Отметка', value: (item) => item.mark ?? '', width: '30%' },
  dueColumn,
  responsibleColumn,
  {
    id: 'status',
    header: 'Статус',
    cell: (item) => <StatusBadge item={item} />,
    width: '12rem',
    hideable: false,
  },
];

const viewTexts = {
  open: {
    note: 'Открытые пункты разобранных протоколов со статусом на дату отсчёта',
    label: 'Открытые пункты',
    emptyTitle: 'Открытых пунктов нет',
    emptyText: 'Пункты появятся после разбора протокола на странице «Документы»',
  },
  aiClosed: {
    note: 'Пункты, которые ИИ закрыл по примечанию или тексту срока, с уверенностью решения',
    label: 'Закрытые ИИ пункты',
    emptyTitle: 'Закрытых ИИ пунктов нет',
    emptyText: 'ИИ закрывает пункт, если по примечанию он выполнен с вероятностью не ниже порога',
  },
  markClosed: {
    note: 'Пункты, которые закрыла отметка «выполнено» или «готово» в ячейке срока или примечании',
    label: 'Закрытые отметкой пункты',
    emptyTitle: 'Закрытых отметкой пунктов нет',
    emptyText: '«Выполнено» или «готово» в ячейке срока или примечании закрывает пункт правилом',
  },
} as const satisfies Record<ControlView, Record<string, string>>;

/** «Статус: все», «Статус: просрочено, горит», «Статус: выбрано 3». */
export function groupsLabel(groups: readonly ControlGroupFilter[]): string {
  if (groups.length === 0) return 'все';
  if (groups.length > 2) return `выбрано ${groups.length}`;
  return groups.map((group) => lowerFirst(controlGroupFilterLabels[group])).join(', ');
}

/**
 * Подпись кнопки «Дата отсчёта» (003, сценарий 2.7; 047, DNS-04): «на сегодня, 04.11.2026» —
 * сегодня в поясе установки; «по листу, 19.10.2026» — если у пунктов на экране одна дата отсчёта;
 * иначе — «по листу». today — «2026-11-04».
 */
export function asOfButtonLabel(
  mode: AsOfMode,
  items: readonly Pick<ControlItem, 'asOf'>[],
  today: string,
): string {
  if (mode === 'today') return `на сегодня, ${formatDate(today)}`;
  const dates = new Set(items.map((item) => item.asOf));
  const [only] = dates;
  return dates.size === 1 && only ? `по листу, ${formatDate(only)}` : 'по листу';
}

/**
 * Число на «Фильтры» телефона (046, PHN-07): «Статус» (в «Открытых»), «Ответственный»,
 * «Документ», «Срок до» — по одному на условие. Поиск, дата отсчёта и сортировка список не
 * сужают или видны сами — не считаются.
 */
export function controlFilterCount(search: ControlSearch): number {
  const view = search.view ?? 'open';
  return [
    view === 'open' && parseGroups(search.group).length > 0,
    Boolean(search.responsibleId),
    Boolean(search.documentId),
    Boolean(search.dueBefore),
  ].filter(Boolean).length;
}

/** «Проверить (ИИ)» → «проверить (ИИ)»: сокращение остаётся прописным. */
function lowerFirst(text: string): string {
  return text.charAt(0).toLocaleLowerCase('ru-RU') + text.slice(1);
}

export interface ControlPageProps {
  search: ControlSearch;
  onSearchChange: (next: ControlSearch) => void;
}

/**
 * Имя записи фильтра «Ответственный» для кнопки: выбранное в панели или из ответственных
 * найденных пунктов — все они с этой записью.
 */
export function responsibleName(
  id: string | undefined,
  items: readonly ControlItem[],
  chosen: { id: string; name: string } | null,
): string | undefined {
  if (!id) return undefined;
  if (chosen?.id === id) return chosen.name;
  for (const item of items) {
    const found = item.responsibles?.find((responsible) => responsible.record?.id === id);
    if (found?.record) return found.record.name;
  }
  return undefined;
}

export function ControlPage({ search, onSearchChange }: ControlPageProps) {
  const me = useMe();
  const timeZone = useTimeZone();
  const [chosen, setChosen] = useState<{ id: string; name: string } | null>(null);
  const view = search.view ?? 'open';
  const asOf = search.asOf ?? 'sheet';
  const sort = search.sort ?? 'source';
  const groups = view === 'open' ? parseGroups(search.group) : [];
  const texts = viewTexts[view];
  const tableColumns =
    view === 'open' ? openColumns : view === 'markClosed' ? markClosedColumns : aiClosedColumns;
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  // 046: окно «Фильтры» телефона открывают кнопка и чипы; фокус после ✕ последнего чипа — на кнопку.
  const phone = useIsPhone();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLButtonElement>(null);
  // Шире 640 px окна нет: открытое закрывается и не встаёт снова при возврате на телефон;
  // условия остаются в адресе (spec 046, «Граничные случаи»).
  if (!phone && filtersOpen) setFiltersOpen(false);

  // Поиск идёт на каждое нажатие с короткой задержкой; строка поиска — в адресе.
  const [text, setText] = useState(search.q ?? '');
  const [syncedQ, setSyncedQ] = useState(search.q);
  if (search.q !== syncedQ) {
    setSyncedQ(search.q);
    setText(search.q ?? '');
  }
  useEffect(() => {
    const q = text.trim() === '' ? undefined : text;
    if (q === search.q) return undefined;
    const timer = window.setTimeout(
      () => onSearchChange(normalizeControlSearch({ ...search, q })),
      SEARCH_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [text, search, onSearchChange]);

  const set = (patch: Partial<ControlSearch>) =>
    onSearchChange(normalizeControlSearch({ ...search, ...patch }));
  const filtered = hasControlFilters(search);
  const reset = () => {
    setText('');
    onSearchChange(withoutFilters(search));
  };

  const documents = useQuery(apiQuery('documents.list'));
  const {
    failure: documentsFailure,
    retrying: documentsRetrying,
    retry: retryDocuments,
  } = useQueryFailure(documents);
  // Варианты «Документа» — из списка документов: пока его нет — строка в меню под «Все
  // документы», а не одна она: загрузка (текст — после 0,3 с), сбой с кодом и «Повторить» (меню
  // остаётся открытым, варианты придут на месте; T150, T161).
  let documentsMessage: ReactNode = null;
  if (documentsFailure && !documentsRetrying) {
    documentsMessage = (
      <DropdownMessage tone="error" code={documentsFailure.code} onRetry={retryDocuments}>
        Не удалось загрузить документы
      </DropdownMessage>
    );
  } else if (documents.isPending) {
    documentsMessage = <DropdownMessage tone="loading">Загружаем документы…</DropdownMessage>;
  }
  const list = useQuery({
    ...apiQuery('control.items', { query: controlQuery(search) }),
    placeholderData: keepPreviousData,
    // Пункты уже запросил загрузчик маршрута (045): не загрузились — страница показывает ошибку
    // с «Повторить», а не повторяет тот же запрос сама при первой отрисовке.
    retryOnMount: false,
  });
  // 045 (OPN-21): первые 0,3 с ожидания — место без рамки таблицы, дальше — скелет строк.
  const waitShown = useDelayedFlag(list.isPending);
  // 010 (FR-048): закрытый отметкой выполнения закрыло правило, а не ИИ — в «Закрыто ИИ» его нет.
  const items = useMemo(
    () =>
      (list.data?.items ?? []).filter((item) => view !== 'aiClosed' || item.closedBy !== 'mark'),
    [list.data, view],
  );
  const protocols = useMemo(() => groupByProtocol(items), [items]);
  // Панель показывает пункт из текущего списка: ушёл из списка по условиям — панель закрыта.
  const selected = useMemo(
    () => (selectedKey ? (items.find((item) => itemKey(item) === selectedKey) ?? null) : null),
    [items, selectedKey],
  );

  const documentRows = (documents.data?.items ?? []).filter(
    (row) => row.lastParsedVersionId !== null || row.mode === 'maintained',
  );
  const documentNumber =
    documentRows.find((row) => row.id === search.documentId)?.number ??
    items.find((item) => item.documentId === search.documentId)?.documentNumber;

  // Выбранная дата отсчёта — на кнопке панели, а не пилюлей под заголовком (047, DNS-03, DNS-04).
  // Пока идёт запрос с новыми условиями, на экране прежние пункты: дата листа — после ответа.
  const asOfText = asOfButtonLabel(asOf, list.isPlaceholderData ? [] : items, todayIn(timeZone));

  const toggleGroup = (group: ControlGroupFilter, checked: boolean) => {
    const next = controlGroupFilters.filter((value) =>
      value === group ? checked : groups.includes(value),
    );
    set({ group: next.length > 0 ? next.join(',') : undefined });
  };

  const hideable = tableColumns.filter((column, index) => index > 0 && column.hideable !== false);

  const responsibleLabel = responsibleName(search.responsibleId, items, chosen);
  const documentLabel = search.documentId
    ? documentNumber
      ? `П-${documentNumber}`
      : 'выбранный'
    : 'все';

  // Условия панели — одни и те же кнопки-меню шире 640 px и рядами окна «Фильтры» на телефоне
  // (046, research Р4: тексты, варианты и порядок — от экрана, а не от окна; MUST ux.md, § 15).
  const filters = (
    <>
      {view === 'open' ? (
        <Dropdown
          label="Статус"
          trigger={
            <Button variant="ghost" size="sm" className={cx(groups.length > 0 && 'kp-on')}>
              Статус: {groupsLabel(groups)}
            </Button>
          }
        >
          <DropdownLabel>Статус</DropdownLabel>
          {controlGroupFilters.map((group) => (
            <DropdownCheckboxItem
              key={group}
              checked={groups.includes(group)}
              onCheckedChange={(checked) => toggleGroup(group, checked)}
            >
              {controlGroupFilterLabels[group]}
            </DropdownCheckboxItem>
          ))}
        </Dropdown>
      ) : null}
      {readsDirectory(me.permissions) ? (
        <ResponsibleFilter
          value={search.responsibleId}
          valueLabel={responsibleLabel}
          onChange={(record) => {
            setChosen(record ?? null);
            set({ responsibleId: record?.id });
          }}
        />
      ) : null}
      <FilterMenu
        label="Документ"
        valueLabel={documentLabel}
        active={Boolean(search.documentId)}
        value={search.documentId ?? ''}
        onChange={(value) => set({ documentId: value || undefined })}
        message={documentsMessage}
        options={[
          ['', 'Все документы'],
          ...documentRows.map((row): [string, string] => [
            row.id,
            `П-${row.number} · ${row.title}`,
          ]),
        ]}
      />
      <DueFilter
        value={search.dueBefore}
        timeZone={timeZone}
        onChange={(dueBefore) => set({ dueBefore })}
      />
      <FilterMenu
        label="Дата отсчёта"
        valueLabel={asOfText}
        // Дата отсчёта не сужает список: не подсвечена и «Сбросить фильтры» её не трогает.
        active={false}
        value={asOf}
        onChange={(value) => set({ asOf: value === 'today' ? 'today' : undefined })}
        options={Object.entries(asOfLabels)}
      />
      <Dropdown
        label="Сортировка"
        trigger={
          <Button variant="ghost" size="sm" icon={ArrowUpDown}>
            Сортировка: {controlSortLabels[sort].toLocaleLowerCase('ru-RU')}
          </Button>
        }
      >
        <DropdownLabel>Порядок пунктов</DropdownLabel>
        <DropdownRadioGroup<string>
          value={sort}
          onValueChange={(value) => set({ sort: value === 'due' ? 'due' : undefined })}
        >
          {(Object.keys(controlSortLabels) as ControlSort[]).map((value) => (
            <DropdownRadioItem key={value} value={value}>
              {controlSortLabels[value]}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      </Dropdown>
    </>
  );

  const searchField = (
    <SearchField
      label="Поиск по тексту, ответственному или примечанию"
      // Целиком в 400 px при 1280 (чек-лист «Табеля»); полностью — в подписи поля.
      placeholder="Найти по тексту, ответственному, примечанию"
      className="kp-toolbar-search kp-control-search"
      maxLength={CONTROL_SEARCH_MAX}
      value={text}
      onChange={(event) => setText(event.target.value)}
    />
  );

  // 046 (T023; PHN-21): чипы телефона — включённые условия фразой «Поле — значение»; нажатие
  // открывает окно, ✕ снимает условие. «Дата отсчёта» — чипом, только когда она не «по листу»:
  // ✕ возвращает «по листу». Сброс в конце ленты — тот же «Сбросить фильтры», что шире 640 px.
  const chip = (key: string, field: string, value: string, patch: Partial<ControlSearch>) => (
    <FilterChip
      key={key}
      field={field}
      removeLabel={`Убрать условие «${field}»`}
      onEdit={() => setFiltersOpen(true)}
      onRemove={() => set(patch)}
    >
      {value}
    </FilterChip>
  );
  const chips = phone
    ? [
        view === 'open' && groups.length > 0
          ? chip('group', 'Статус', groupsLabel(groups), { group: undefined })
          : null,
        search.responsibleId
          ? chip('responsible', 'Ответственный', responsibleLabel ?? 'выбран', {
              responsibleId: undefined,
            })
          : null,
        search.documentId
          ? chip('document', 'Документ', documentLabel, { documentId: undefined })
          : null,
        search.dueBefore
          ? chip('due', 'Срок до', formatDate(search.dueBefore), { dueBefore: undefined })
          : null,
        asOf === 'today'
          ? chip('asOf', 'Дата отсчёта', asOfLabels.today.toLocaleLowerCase('ru-RU'), {
              asOf: undefined,
            })
          : null,
      ].filter((node) => node !== null)
    : [];
  const filterCount = controlFilterCount(search);

  const toolbar = phone ? (
    // 046 (T022; PHN-19): до 640 px — одна строка «поиск · Фильтры», условия — в окне снизу.
    <div className="t-toolbar kp-control-phone-bar">
      {searchField}
      <PhoneFilters
        count={filterCount}
        resetLabel="Сбросить фильтры"
        onReset={filterCount > 0 ? reset : null}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        triggerRef={filtersRef}
      >
        {filters}
      </PhoneFilters>
    </div>
  ) : (
    <div className="t-toolbar">
      {searchField}
      {filters}
      <Dropdown
        label="Колонки"
        trigger={
          <Button variant="ghost" size="sm" icon={Columns3} className="kp-hide-phone">
            Колонки
          </Button>
        }
      >
        <DropdownLabel>Колонки</DropdownLabel>
        {hideable.map((column) => (
          <DropdownCheckboxItem
            key={column.id}
            checked={visibility[column.id] !== false}
            onCheckedChange={(checked) => setVisibility({ ...visibility, [column.id]: checked })}
          >
            {column.header}
          </DropdownCheckboxItem>
        ))}
      </Dropdown>
      {filtered ? (
        <Button variant="ghost" size="sm" onClick={reset}>
          Сбросить фильтры
        </Button>
      ) : null}
    </div>
  );

  const loadError = list.error ? errorView(list.error) : null;
  let body: ReactNode;
  if (loadError) {
    body = (
      <div className="kp-control-empty">
        <LoadErrorState
          view={loadError}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      </div>
    );
  } else if (list.isPending) {
    body = <TableSkeleton shown={waitShown} className="kp-control-table" />;
  } else if (protocols.length === 0) {
    body = (
      <div className="kp-control-empty">
        {filtered ? (
          <EmptyState
            icon={ClipboardList}
            title="Ничего не найдено"
            text="Нет пунктов по этим условиям"
            action={<Button onClick={reset}>Сбросить фильтры</Button>}
          />
        ) : (
          <EmptyState
            icon={view === 'open' ? ClipboardList : view === 'markClosed' ? CheckCheck : Sparkles}
            title={texts.emptyTitle}
            text={texts.emptyText}
          />
        )}
      </div>
    );
  } else {
    body = protocols.map((protocol) => (
      <section key={protocol.key} className="kp-control-doc" aria-label={protocol.title}>
        <div className="kp-control-doc-h">
          <h2>{protocol.title}</h2>
          <span className="t-cnt t-num">{protocol.count}</span>
          {protocol.overdue > 0 ? (
            <span className="kp-control-count">
              <Icon icon={AlarmClock} />
              просрочено <span className="t-num">{protocol.overdue}</span>
            </span>
          ) : null}
          {protocol.burning > 0 ? (
            <span className="kp-control-count">
              <Icon icon={Flame} />
              горит <span className="t-num">{protocol.burning}</span>
            </span>
          ) : null}
        </div>
        {protocol.sections.map((section) => (
          // Имя разделу дают таблица и её область прокрутки (DataTable): обёртка — без своего.
          <div key={section.key} className="kp-control-sec">
            <div className="kp-control-sec-h">
              <h3>{section.section || 'Без раздела'}</h3>
              <span className="t-cnt t-num">{section.items.length}</span>
            </div>
            <DataTable
              label={section.label}
              columns={tableColumns}
              rows={section.items}
              getRowId={itemKey}
              columnVisibility={visibility}
              onRowClick={(item) => setSelectedKey(itemKey(item))}
              selectedRowId={selectedKey}
              className="kp-control-table"
            />
          </div>
        ))}
      </section>
    ));
  }

  return (
    <main id="main" tabIndex={-1} className="kp-page kp-page-wide">
      <PageHeader title="Контроль" note={texts.note} />
      <div className="kp-control-view">
        <Segmented<ControlView>
          label="Пункты"
          value={view}
          onChange={(next) => {
            setSelectedKey(null);
            set({ view: next });
          }}
          options={viewOptions}
        />
      </div>
      <div className="t-card kp-control-board">
        {toolbar}
        {/* Лента остаётся в дереве и без чипов: после ✕ последнего она ставит фокус на «Фильтры». */}
        {/* Сброс — когда он есть и шире 640 px: одна «Дата отсчёта» без поиска им не снимается. */}
        <PhoneChips
          resetLabel="Сбросить фильтры"
          onReset={filtered ? reset : null}
          filtersRef={filtersRef}
        >
          {chips}
        </PhoneChips>
        {body}
      </div>
      <ItemDrawer item={selected} onClose={() => setSelectedKey(null)} />
    </main>
  );
}

/**
 * «Контроль», пока пункты идут дольше 300 мс от перехода (045, OPN-21; ревью: T041) — каркас
 * страницы: заголовок с пояснением вида, место переключателя вида и рамка списка сразу со строками
 * скелета в форме таблицы, а не общий скелет страницы. Его показывает маршрутизатор
 * (pendingComponent маршрута /control): 300 мс уже прошли — строки сразу.
 */
export function ControlPending({ search }: { search: ControlSearch }) {
  const texts = viewTexts[search.view ?? 'open'];
  return (
    <main id="main" tabIndex={-1} className="kp-page kp-page-wide">
      <PageHeader title="Контроль" note={texts.note} />
      <div className="kp-control-view" aria-hidden="true">
        <SkeletonBar width={320} height={28} />
      </div>
      <div className="t-card kp-control-board">
        <TableSkeleton className="kp-control-table" />
      </div>
    </main>
  );
}

function FilterMenu({
  label,
  valueLabel,
  value,
  active = value !== '',
  options,
  onChange,
  message,
}: {
  label: string;
  valueLabel: string;
  value: string;
  /** Подсвечен, когда условие сужает список (ux.md, раздел 6). */
  active?: boolean;
  options: readonly (readonly [string, string])[];
  onChange: (value: string) => void;
  /** Строка-сообщение под вариантами: варианты грузятся или не загрузились (DropdownMessage). */
  message?: ReactNode;
}) {
  return (
    <Dropdown
      label={label}
      trigger={
        <Button variant="ghost" size="sm" className={cx(active && 'kp-on')}>
          {label}: {valueLabel}
        </Button>
      }
    >
      <DropdownLabel>{label}</DropdownLabel>
      <DropdownRadioGroup<string> value={value} onValueChange={onChange}>
        {options.map(([optionValue, optionLabel]) => (
          <DropdownRadioItem key={optionValue || 'all'} value={optionValue}>
            {optionLabel}
          </DropdownRadioItem>
        ))}
      </DropdownRadioGroup>
      {message}
    </Dropdown>
  );
}

/**
 * «Срок до»: быстрые пилюли и дата в поясе установки; срок не позже даты, включительно. Дата —
 * полем «дд.мм.гггг» (README «Тексты»), как у фильтра периода (period-filter.tsx): условие
 * меняется, как только дата введена целиком или поле очищено; неполная («20.1») проверяется
 * по Enter — ошибка под полем с примером, фокус в поле, введённое не стирается.
 */
function DueFilter({
  value,
  timeZone,
  onChange,
}: {
  value: string | undefined;
  timeZone: string;
  onChange: (value: string | undefined) => void;
}) {
  const [open, setOpenState] = useState(false);
  /** В поле неполная или несуществующая дата; ошибка видна после Enter до первой верной даты. */
  const [incomplete, setIncomplete] = useState(false);
  const [shown, setShown] = useState(false);
  // Панель закрылась — неполный ввод ушёл вместе с полем: при следующем открытии — дата условия.
  const setOpen = (next: boolean) => {
    setOpenState(next);
    setIncomplete(false);
    setShown(false);
  };
  const today = todayIn(timeZone);
  const presets: [string, string][] = [
    ['Сегодня', today],
    ['Завтра', shiftDate(today, 1)],
    ['След. неделя', shiftDate(today, 7)],
    ['Через 2 недели', shiftDate(today, 14)],
  ];
  return (
    <Popover
      label="Срок до"
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          icon={CalendarClock}
          className={cx(value !== undefined && 'kp-on')}
        >
          Срок до: {value ? formatDate(value) : 'любой'}
        </Button>
      }
    >
      <div className="t-col kp-period">
        <div className="t-row">
          {presets.map(([name, date]) => (
            <Button
              key={name}
              size="sm"
              onClick={() => {
                onChange(date);
                setOpen(false);
              }}
            >
              {name}
            </Button>
          ))}
        </div>
        <DateField
          label="Срок не позже"
          value={value ?? ''}
          today={today}
          error={shown ? fieldMessages.date : undefined}
          onChange={(next, input) => {
            setIncomplete(input.invalid);
            if (input.invalid) return;
            setShown(false);
            onChange(next || undefined);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (incomplete) setShown(true);
            else setOpen(false);
          }}
        />
        {value ? (
          <Button
            variant="ghost"
            size="sm"
            className="kp-danger-text"
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
          >
            Очистить
          </Button>
        ) : null}
      </div>
    </Popover>
  );
}
